<script setup>
// Home (modelada e aprovada no prototipo): a tela responde "o que precisa de
// mim agora", nao "quanto acumulamos desde sempre". Contadores historicos
// sairam; venda usa a regua da engrenagem, igual ao resto do produto.
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { usePermissions } from 'dashboard/composables/usePermissions';

const store = useStore();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const { canEditPipeline } = usePermissions();

const currentUser = useMapGetter('getCurrentUser');
const currentRole = useMapGetter('getCurrentRole');
const isAdmin = computed(() => currentRole.value === 'administrator');
const canSeeOverview = computed(() => isAdmin.value || canEditPipeline.value);
const userName = computed(
  () => (currentUser.value?.name || '').split(' ')[0] || 'por aqui'
);

const activeView = ref('agent');
const switchView = view => {
  activeView.value = view;
};

const todayFormatted = computed(() => {
  const d = new Date();
  return d.toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
});

const greeting = computed(() => {
  const h = new Date().getHours();
  if (h < 12) return 'Bom dia';
  if (h < 18) return 'Boa tarde';
  return 'Boa noite';
});

// ── Dados (agregados no servidor) ──
const myStats = useMapGetter('dashboardHome/getMyStats');
const companyStats = useMapGetter('dashboardHome/getCompanyStats');
const leaderboard = useMapGetter('dashboardHome/getLeaderboard');
const pipelines = useMapGetter('dashboardHome/getPipelines');
const alerts = useMapGetter('dashboardHome/getAlerts');
const myStalledDeals = useMapGetter('dashboardHome/getMyStalledDeals');
const myTasks = useMapGetter('dashboardHome/getMyTasks');
const myMeetings = useMapGetter('dashboardHome/getMyMeetings');

onMounted(() => {
  store.dispatch('dashboardHome/fetchHomeStats').then(() => {
    // Quem tem carteira comeca pelo proprio trabalho; quem so supervisiona,
    // pelo panorama.
    const mine = myStats.value || {};
    const hasWork =
      (mine.active_deals || 0) > 0 || (mine.pending_tasks || 0) > 0;
    if (canSeeOverview.value && !hasWork) activeView.value = 'admin';
  });
});

// ── Formatadores ──
const nf = v => Number(v || 0).toLocaleString('pt-BR');
const money = value => {
  const n = Number(value) || 0;
  if (n >= 1e6)
    return `R$ ${(n / 1e6).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mi`;
  if (n >= 1000)
    return `R$ ${(n / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil`;
  return `R$ ${Math.round(n).toLocaleString('pt-BR')}`;
};
const pct = (part, whole) => (whole > 0 ? Math.round((part / whole) * 100) : 0);

const delta = (now, before) => {
  if (!before) return null;
  return Math.round(((now - before) / before) * 100);
};

// ── Meu painel ──
const myKpis = computed(() => {
  const s = myStats.value || {};
  const out = [];
  if (s.overdue_tasks > 0) {
    out.push({
      key: 'overdue',
      label: 'Tarefas vencidas',
      value: nf(s.overdue_tasks),
      sub: `de ${nf(s.pending_tasks)} pendentes`,
      tone: 'bad',
    });
  } else {
    out.push({
      key: 'tasks',
      label: 'Tarefas de hoje',
      value: nf(s.today_tasks),
      sub: `${nf(s.pending_tasks)} pendentes no total`,
    });
  }
  out.push({
    key: 'stalled',
    label: 'Deals parados',
    value: nf(s.stalled_deals),
    sub: `de ${nf(s.active_deals)} na sua carteira`,
    tone: s.stalled_deals > 0 ? 'warn' : null,
  });
  out.push({
    key: 'conversations',
    label: 'Conversas na sua fila',
    value: nf(s.open_conversations),
    sub: `${nf(s.unattended_conversations)} sem resposta`,
    tone: s.unattended_conversations > 0 ? 'warn' : null,
  });
  out.push({
    key: 'won',
    label: 'Suas vendas no mês',
    value: nf(s.won_count),
    sub: s.team_won_count
      ? `${money(s.won_value)} · ${pct(s.won_count, s.team_won_count)}% do time`
      : money(s.won_value),
    tone: 'good',
  });
  return out;
});

// Lista de acoes: so entra o que realmente precisa de alguem
const myActions = computed(() => {
  const s = myStats.value || {};
  const items = [];
  if (s.overdue_tasks > 0) {
    items.push({
      key: 'overdue',
      icon: '⏰',
      tone: 'bad',
      title: `${nf(s.overdue_tasks)} ${s.overdue_tasks > 1 ? 'tarefas vencidas' : 'tarefa vencida'}`,
      sub: 'Prazo já passou',
      cta: 'Abrir tarefas',
      route: 'crm_tasks',
    });
  }
  if (s.unattended_conversations > 0) {
    items.push({
      key: 'unattended',
      icon: '💬',
      tone: 'warn',
      title: `${nf(s.unattended_conversations)} conversas sem resposta`,
      sub: `De ${nf(s.open_conversations)} atribuídas a você`,
      cta: 'Ir para a caixa',
      route: 'home',
    });
  }
  if (s.stalled_deals > 0) {
    items.push({
      key: 'stalled',
      icon: '🕸️',
      tone: 'warn',
      title: `${nf(s.stalled_deals)} deals parados há mais de 7 dias`,
      sub: 'Sem movimentação de fase',
      cta: 'Ver lista',
      route: 'crm_list',
    });
  }
  if (s.deals_without_value > 0) {
    items.push({
      key: 'no_value',
      icon: '📝',
      tone: 'neutral',
      title: `${nf(s.deals_without_value)} deals seus sem valor preenchido`,
      sub: 'Sem valor eles não entram na previsão',
      cta: 'Preencher',
      route: 'crm_list',
    });
  }
  return items;
});

const teamShare = computed(() => {
  const s = myStats.value || {};
  return pct(s.won_count, s.team_won_count);
});

// ── Visão geral ──
const companyKpis = computed(() => {
  const c = companyStats.value || {};
  const salesDelta = delta(c.month_won_count, c.previous_won_count);
  const valueDelta = delta(c.month_won_value, c.previous_won_value);
  const createdDelta = delta(c.month_created, c.previous_created);
  return [
    {
      key: 'sales',
      label: 'Vendas no mês',
      value: nf(c.month_won_count),
      delta: salesDelta,
      sub: `${nf(c.previous_won_count)} no mês passado`,
      tone: 'good',
    },
    {
      key: 'revenue',
      label: 'Receita do mês',
      value: money(c.month_won_value),
      delta: valueDelta,
      sub: `${money(c.previous_won_value)} no mês passado`,
    },
    {
      key: 'created',
      label: 'Deals criados',
      value: nf(c.month_created),
      delta: createdDelta,
      sub: `${nf(c.previous_created)} no mês passado`,
    },
    {
      key: 'conversion',
      label: 'Conversão do mês',
      value: `${String(c.conversion_pct ?? 0).replace('.', ',')}%`,
      sub: 'de deal criado para venda',
    },
  ];
});

const ALERT_COPY = {
  offline_queue: {
    icon: '🔴',
    tone: 'bad',
    cta: 'Ver equipe',
    route: 'agent_list',
    title: a =>
      `${nf(a.online)} de ${nf(a.total)} agentes online, com ${nf(a.count)} conversas atribuídas`,
    sub: () => 'A fila está com gente que não está na operação agora',
  },
  unassigned: {
    icon: '📥',
    tone: 'bad',
    cta: 'Distribuir',
    route: 'home',
    title: a => `${nf(a.count)} conversas sem nenhum responsável`,
    sub: a => `De ${nf(a.total)} abertas — ninguém foi designado`,
  },
  deals_without_value: {
    icon: '💰',
    tone: 'warn',
    cta: 'Ver deals',
    route: 'crm_list',
    title: a => `${nf(a.count)} dos ${nf(a.total)} deals abertos sem valor`,
    sub: a =>
      `${pct(a.count, a.total)}% do pipeline invisível para qualquer previsão`,
  },
  stalled_deals: {
    icon: '🕸️',
    tone: 'warn',
    cta: 'Ver parados',
    route: 'crm_list',
    title: a => `${nf(a.count)} deals parados há mais de ${a.days} dias`,
    sub: a => `${pct(a.count, a.total)}% do pipeline sem movimentação`,
  },
  overdue_tasks: {
    icon: '⏰',
    tone: 'warn',
    cta: 'Ver tarefas',
    route: 'crm_tasks',
    title: a =>
      `${nf(a.count)} tarefas vencidas e ${nf(a.unassigned)} sem dono`,
    sub: a => `De ${nf(a.total)} tarefas pendentes na conta`,
  },
};

const alertItems = computed(() =>
  (alerts.value || [])
    .map(alert => {
      const copy = ALERT_COPY[alert.key];
      if (!copy) return null;
      return {
        key: alert.key,
        icon: copy.icon,
        tone: copy.tone,
        cta: copy.cta,
        route: copy.route,
        title: copy.title(alert),
        sub: copy.sub(alert),
      };
    })
    .filter(Boolean)
);

const sellers = computed(() => leaderboard.value?.sellers || []);
const maxSellerDeals = computed(() =>
  Math.max(...sellers.value.map(s => s.won_deals), 1)
);
const idleAgents = computed(() => {
  const board = leaderboard.value || {};
  return Math.max(0, (board.agents_total || 0) - (board.sellers_total || 0));
});
const salesWithoutValue = computed(() =>
  sellers.value.reduce((sum, s) => sum + (s.without_value || 0), 0)
);

const pipelineTotals = computed(() => {
  const list = pipelines.value || [];
  return {
    count: list.reduce((sum, p) => sum + p.count, 0),
    value: list.reduce((sum, p) => sum + p.value, 0),
  };
});
const maxPipelineCount = computed(() =>
  Math.max(...(pipelines.value || []).map(p => p.count), 1)
);
const maxPipelineValue = computed(() =>
  Math.max(...(pipelines.value || []).map(p => p.value), 1)
);

const hasMeetings = computed(() => (myMeetings.value || []).length > 0);
const upcoming = computed(() =>
  (myMeetings.value || []).slice(0, 4).map(meeting => ({
    id: meeting.id,
    title: meeting.title || meeting.summary,
    when: meeting.start_at || meeting.start,
  }))
);

const upcomingTasks = computed(() => (myTasks.value || []).slice(0, 4));

const navigateTo = routeName => {
  router.push(accountScopedRoute(routeName));
};

const TONE_ICON = {
  bad: 'bg-red-50 dark:bg-red-900/30',
  warn: 'bg-yellow-50 dark:bg-yellow-900/30',
  good: 'bg-green-50 dark:bg-green-900/30',
  neutral: 'bg-n-alpha-2',
};
const TONE_CARD = {
  bad: 'bg-red-50 dark:bg-red-900/20 outline-transparent',
  warn: 'bg-yellow-50 dark:bg-yellow-900/20 outline-transparent',
  good: 'bg-green-50 dark:bg-green-900/20 outline-transparent',
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <div class="w-full overflow-y-auto bg-n-surface-1">
    <div class="max-w-[1400px] mx-auto px-6 py-6 flex flex-col gap-4">
      <!-- Cabecalho -->
      <div class="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1
            class="font-headline text-[23px] font-bold text-n-slate-12 mb-0.5"
          >
            {{ `${greeting}, ${userName}` }}
          </h1>
          <p class="text-[12.5px] text-n-slate-9 mb-0 first-letter:uppercase">
            {{ todayFormatted }}
          </p>
        </div>
        <div
          v-if="canSeeOverview"
          class="inline-flex rounded-[9px] border border-n-weak overflow-hidden text-xs"
        >
          <button
            class="px-3.5 py-1.5"
            :class="
              activeView === 'agent'
                ? 'bg-n-solid-2 text-n-slate-12 font-semibold'
                : 'text-n-slate-10 hover:text-n-slate-12'
            "
            @click="switchView('agent')"
          >
            Meu painel
          </button>
          <button
            class="px-3.5 py-1.5 border-l border-n-weak"
            :class="
              activeView === 'admin'
                ? 'bg-n-solid-2 text-n-slate-12 font-semibold'
                : 'text-n-slate-10 hover:text-n-slate-12'
            "
            @click="switchView('admin')"
          >
            Visão geral
          </button>
        </div>
      </div>

      <!-- ── MEU PAINEL ── -->
      <template v-if="activeView === 'agent'">
        <div class="flex gap-3.5 flex-wrap">
          <div
            v-for="kpi in myKpis"
            :key="kpi.key"
            class="flex-1 min-w-[168px] rounded-2xl px-4 py-3.5 outline outline-1 outline-n-container"
            :class="TONE_CARD[kpi.tone] || 'bg-n-solid-2'"
          >
            <div class="text-[11px] text-n-slate-9 mb-1.5">{{ kpi.label }}</div>
            <div
              class="font-headline text-[29px] font-bold tabular-nums leading-none text-n-slate-12"
            >
              {{ kpi.value }}
            </div>
            <div class="text-[11px] text-n-slate-9 mt-1.5">{{ kpi.sub }}</div>
          </div>
        </div>

        <div class="flex gap-4 flex-wrap items-start">
          <div
            class="flex-1 min-w-[340px] rounded-2xl bg-n-solid-2 outline outline-1 outline-n-container shadow px-6 py-5"
          >
            <h5
              class="mb-0.5 font-headline font-semibold text-lg text-n-slate-12"
            >
              Precisa de você agora
            </h5>
            <p class="text-[11.5px] text-n-slate-9 mb-2">
              Em ordem de urgência
            </p>
            <div
              v-for="item in myActions"
              :key="item.key"
              class="flex items-center gap-3 py-2.5 border-b border-n-weak last:border-0"
            >
              <span
                class="size-[30px] rounded-[9px] flex items-center justify-center flex-none text-[14px]"
                :class="TONE_ICON[item.tone]"
              >
                {{ item.icon }}
              </span>
              <span class="flex-1 min-w-0">
                <span class="text-[13px] text-n-slate-12 font-medium block">
                  {{ item.title }}
                </span>
                <span class="text-[11.5px] text-n-slate-9">{{ item.sub }}</span>
              </span>
              <button
                class="text-[11.5px] font-semibold text-[#9B2C2C] dark:text-[#F472B6] whitespace-nowrap"
                @click="navigateTo(item.route)"
              >
                {{ `${item.cta} ›` }}
              </button>
            </div>
            <p
              v-if="!myActions.length"
              class="text-[12.5px] text-n-slate-10 py-4 mb-0"
            >
              Nada pendente. Sua carteira está em dia.
            </p>
          </div>

          <div
            class="flex-1 min-w-[320px] rounded-2xl bg-n-solid-2 outline outline-1 outline-n-container shadow px-6 py-5"
          >
            <h5
              class="mb-0.5 font-headline font-semibold text-lg text-n-slate-12"
            >
              Os mais esquecidos
            </h5>
            <p class="text-[11.5px] text-n-slate-9 mb-2">
              Seus deals com mais tempo parados na mesma fase
            </p>
            <div
              v-for="deal in myStalledDeals"
              :key="deal.id"
              class="flex items-center gap-3 py-2.5 border-b border-n-weak last:border-0"
            >
              <span class="flex-1 min-w-0">
                <span
                  class="text-[13px] text-n-slate-12 font-medium block truncate"
                >
                  {{ deal.title }}
                </span>
                <span class="text-[11.5px] text-n-slate-9">
                  {{ deal.stage }}
                </span>
              </span>
              <b
                class="font-headline text-[15px] text-red-600 dark:text-red-400 tabular-nums"
              >
                {{ `${deal.days_in_stage}d` }}
              </b>
            </div>
            <p
              v-if="!myStalledDeals.length"
              class="text-[12.5px] text-n-slate-10 py-4 mb-0"
            >
              Nenhum deal parado. Tudo se movendo.
            </p>
          </div>
        </div>

        <div class="flex gap-4 flex-wrap items-start">
          <div
            v-if="hasMeetings || upcomingTasks.length"
            class="flex-1 min-w-[320px] rounded-2xl bg-n-solid-2 outline outline-1 outline-n-container shadow px-6 py-5"
          >
            <h5
              class="mb-2.5 font-headline font-semibold text-lg text-n-slate-12"
            >
              Sua agenda
            </h5>
            <div
              v-for="meeting in upcoming"
              :key="meeting.id"
              class="flex items-center gap-3 py-2 border-b border-n-weak last:border-0"
            >
              <span class="text-[13px] text-n-slate-12 flex-1 truncate">
                {{ meeting.title }}
              </span>
              <span class="text-[11.5px] text-n-slate-9">{{
                meeting.when
              }}</span>
            </div>
            <div
              v-for="task in upcomingTasks"
              :key="`t-${task.id}`"
              class="flex items-center gap-3 py-2 border-b border-n-weak last:border-0"
            >
              <span class="text-[13px] text-n-slate-12 flex-1 truncate">
                {{ task.title }}
              </span>
              <span
                class="text-[11.5px]"
                :class="
                  task.overdue
                    ? 'text-red-600 dark:text-red-400 font-semibold'
                    : 'text-n-slate-9'
                "
              >
                {{ task.overdue ? 'vencida' : 'tarefa' }}
              </span>
            </div>
          </div>

          <div
            class="flex-1 min-w-[320px] rounded-2xl bg-n-solid-2 outline outline-1 outline-n-container shadow px-6 py-5"
          >
            <h5
              class="mb-2.5 font-headline font-semibold text-lg text-n-slate-12"
            >
              Seu mês
            </h5>
            <div class="flex items-baseline gap-2.5 mb-2.5">
              <span
                class="font-headline text-[27px] font-bold text-green-600 dark:text-green-400 tabular-nums"
              >
                {{ money(myStats.won_value) }}
              </span>
              <span class="text-[12.5px] text-n-slate-10">
                {{ `em ${nf(myStats.won_count)} vendas` }}
              </span>
            </div>
            <div class="h-2 rounded-full bg-n-slate-3 overflow-hidden mb-2">
              <span
                class="block h-full rounded-full bg-green-500"
                :style="{ width: `${teamShare}%` }"
              />
            </div>
            <p class="text-[11.5px] text-n-slate-9 mb-0">
              {{
                `Você responde por ${teamShare}% das ${nf(myStats.team_won_count)} vendas do time neste mês`
              }}
            </p>
          </div>
        </div>
      </template>

      <!-- ── VISÃO GERAL ── -->
      <template v-else>
        <div class="flex gap-3.5 flex-wrap">
          <div
            v-for="kpi in companyKpis"
            :key="kpi.key"
            class="flex-1 min-w-[168px] rounded-2xl px-4 py-3.5 outline outline-1 outline-n-container"
            :class="TONE_CARD[kpi.tone] || 'bg-n-solid-2'"
          >
            <div class="text-[11px] text-n-slate-9 mb-1.5">{{ kpi.label }}</div>
            <div class="flex items-baseline justify-between gap-2">
              <span
                class="font-headline text-[29px] font-bold tabular-nums leading-none text-n-slate-12"
              >
                {{ kpi.value }}
              </span>
              <span
                v-if="kpi.delta !== null && kpi.delta !== undefined"
                class="px-1.5 py-px rounded-full text-[11px] font-bold"
                :class="
                  kpi.delta >= 0
                    ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                "
              >
                {{ kpi.delta >= 0 ? '▲' : '▼' }}
                {{ `${Math.abs(kpi.delta)}%` }}
              </span>
            </div>
            <div class="text-[11px] text-n-slate-9 mt-1.5">{{ kpi.sub }}</div>
          </div>
        </div>

        <div
          v-if="alertItems.length"
          class="rounded-2xl bg-n-solid-2 outline outline-1 outline-n-container shadow px-6 py-5"
        >
          <h5
            class="mb-0.5 font-headline font-semibold text-lg text-n-slate-12"
          >
            Exige atenção
          </h5>
          <p class="text-[11.5px] text-n-slate-9 mb-2">
            O que está travando a operação agora, do mais grave ao menos
          </p>
          <div
            v-for="item in alertItems"
            :key="item.key"
            class="flex items-center gap-3 py-2.5 border-b border-n-weak last:border-0"
          >
            <span
              class="size-[30px] rounded-[9px] flex items-center justify-center flex-none text-[14px]"
              :class="TONE_ICON[item.tone]"
            >
              {{ item.icon }}
            </span>
            <span class="flex-1 min-w-0">
              <span class="text-[13px] text-n-slate-12 font-medium block">
                {{ item.title }}
              </span>
              <span class="text-[11.5px] text-n-slate-9">{{ item.sub }}</span>
            </span>
            <button
              class="text-[11.5px] font-semibold text-[#9B2C2C] dark:text-[#F472B6] whitespace-nowrap"
              @click="navigateTo(item.route)"
            >
              {{ `${item.cta} ›` }}
            </button>
          </div>
        </div>

        <div class="flex gap-4 flex-wrap items-start">
          <div
            class="flex-1 min-w-[400px] rounded-2xl bg-n-solid-2 outline outline-1 outline-n-container shadow px-6 py-5"
          >
            <div class="flex items-baseline justify-between gap-3 flex-wrap">
              <h5
                class="mb-0 font-headline font-semibold text-lg text-n-slate-12"
              >
                Pipeline da empresa
              </h5>
              <span class="text-[11.5px] text-n-slate-9">
                {{ `todos os ${pipelines.length} funis` }}
              </span>
            </div>
            <p class="text-[11.5px] text-n-slate-9 mb-3">
              {{
                `${nf(pipelineTotals.count)} deals abertos · ${money(pipelineTotals.value)} — quanto cada funil representa do total`
              }}
            </p>
            <div
              class="grid grid-cols-[1fr_96px_1fr] gap-x-3 gap-y-1.5 items-center"
            >
              <span
                class="text-[9.5px] font-bold uppercase tracking-[0.07em] text-n-slate-9 pb-1 border-b border-n-weak"
              >
                Funil
              </span>
              <span
                class="text-[9.5px] font-bold uppercase tracking-[0.07em] text-n-slate-9 pb-1 border-b border-n-weak text-center"
              >
                Deals abertos
              </span>
              <span
                class="text-[9.5px] font-bold uppercase tracking-[0.07em] text-n-slate-9 pb-1 border-b border-n-weak"
              >
                Valor em aberto
              </span>
              <template v-for="pipeline in pipelines" :key="pipeline.id">
                <span
                  class="text-[12.5px] text-n-slate-11 truncate"
                  :title="pipeline.name"
                >
                  {{ pipeline.name }}
                </span>
                <span class="flex items-center gap-2 justify-end">
                  <span
                    class="flex-1 h-[7px] rounded-full bg-n-slate-3 overflow-hidden"
                  >
                    <span
                      class="block h-full rounded-full bg-[#6B8AAF]"
                      :style="{
                        width: `${pct(pipeline.count, maxPipelineCount)}%`,
                      }"
                    />
                  </span>
                  <b
                    class="font-headline text-[13px] text-n-slate-12 tabular-nums min-w-[34px] text-right"
                  >
                    {{ nf(pipeline.count) }}
                  </b>
                </span>
                <span class="flex items-center gap-2">
                  <span
                    class="flex-1 h-[7px] rounded-full bg-n-slate-3 overflow-hidden"
                  >
                    <span
                      class="block h-full rounded-full bg-green-500"
                      :style="{
                        width: `${pct(pipeline.value, maxPipelineValue)}%`,
                      }"
                    />
                  </span>
                  <b
                    class="font-headline text-[12.5px] text-n-slate-12 tabular-nums min-w-[56px] text-right"
                  >
                    {{ money(pipeline.value) }}
                  </b>
                </span>
              </template>
            </div>
            <div
              class="flex gap-4 flex-wrap mt-3 pt-2.5 border-t border-n-weak text-[11px] text-n-slate-9"
            >
              <span class="inline-flex items-center gap-1.5">
                <i class="size-2 rounded-sm bg-[#6B8AAF]" />
                barra azul = fatia dos deals abertos
              </span>
              <span class="inline-flex items-center gap-1.5">
                <i class="size-2 rounded-sm bg-green-500" />
                barra verde = fatia do valor
              </span>
            </div>
          </div>

          <div
            class="flex-1 min-w-[330px] rounded-2xl bg-n-solid-2 outline outline-1 outline-n-container shadow px-6 py-5"
          >
            <div class="flex items-baseline justify-between gap-3 flex-wrap">
              <h5
                class="mb-0 font-headline font-semibold text-lg text-n-slate-12"
              >
                Ranking do mês
              </h5>
              <span class="text-[11.5px] text-n-slate-9">
                {{
                  `${nf(companyStats.month_won_count)} vendas · ${money(companyStats.month_won_value)}`
                }}
              </span>
            </div>
            <p class="text-[11.5px] text-n-slate-9 mb-3">
              Venda = deal que entrou na fase de ganho configurada, no mês
              corrente
            </p>
            <div
              v-for="(seller, idx) in sellers"
              :key="seller.user_id"
              class="grid grid-cols-[16px_1fr_auto_auto] gap-2.5 items-center py-2 border-b border-n-weak last:border-0"
            >
              <span
                class="font-headline text-[12px] text-n-slate-9 text-center"
              >
                {{ idx + 1 }}
              </span>
              <span class="min-w-0">
                <span
                  class="text-[13px] font-semibold text-n-slate-12 block truncate mb-1"
                >
                  {{ seller.name }}
                </span>
                <span class="block h-1.5 rounded-full bg-n-slate-3">
                  <span
                    class="block h-full rounded-full bg-green-500"
                    :style="{
                      width: `${pct(seller.won_deals, maxSellerDeals)}%`,
                    }"
                  />
                </span>
              </span>
              <span class="text-right whitespace-nowrap">
                <span class="text-[12px] text-n-slate-10 block">
                  <b class="font-headline text-[14px] text-n-slate-12">
                    {{ nf(seller.won_deals) }}
                  </b>
                  {{ seller.won_deals > 1 ? 'vendas' : 'venda' }}
                </span>
                <span
                  class="text-[10.5px]"
                  :class="
                    seller.previous_won
                      ? seller.won_deals >= seller.previous_won
                        ? 'text-green-600 dark:text-green-400 font-semibold'
                        : 'text-red-600 dark:text-red-400 font-semibold'
                      : 'text-n-slate-9'
                  "
                >
                  {{
                    seller.previous_won
                      ? `${seller.won_deals >= seller.previous_won ? '▲' : '▼'} ${Math.abs(seller.won_deals - seller.previous_won)} vs mês passado`
                      : 'novo no mês'
                  }}
                </span>
              </span>
              <span class="min-w-[74px] text-right">
                <b
                  v-if="seller.without_value < seller.won_deals"
                  class="font-headline text-[13.5px] text-n-slate-12"
                >
                  {{ money(seller.won_value) }}
                </b>
                <span
                  v-else
                  class="text-[11px] font-semibold text-yellow-700 dark:text-yellow-500"
                >
                  ⚠ sem valor
                </span>
              </span>
            </div>
            <p
              v-if="idleAgents > 0"
              class="text-[11.5px] text-n-slate-9 pt-2.5 mt-0.5 border-t border-n-weak mb-0"
            >
              {{
                `+ ${nf(idleAgents)} dos ${nf(leaderboard.agents_total)} agentes sem nenhuma venda no mês`
              }}
            </p>
            <p
              v-if="salesWithoutValue > 0"
              class="text-[11px] text-yellow-700 dark:text-yellow-500 mt-2 mb-0"
            >
              {{
                `⚠ ${nf(salesWithoutValue)} das ${nf(companyStats.month_won_count)} vendas estão sem valor preenchido — a receita real pode ser maior.`
              }}
            </p>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
